// Desligar pessoa = data de saída que vale para tudo (28/09/2026).
// O servidor bloqueia o login na hora, desativa os perfis, para as férias
// futuras, move os subordinados e deixa a ficha inativa a partir da data.
// Aqui só se conversa com as duas recusas do pré-voo: horas lançadas depois
// da data (corrija ou mude a data) e subordinados sem novo chefe.
import { useEffect, useMemo, useState } from 'react';
import { Alert, Checkbox, DatePicker, Flex, Modal, Select, Typography, App as AntApp } from 'antd';
import dayjs from 'dayjs';
import { acaoPessoa, ErroPessoa, fmtData, type Opcoes } from './apiPessoas';

const { Text } = Typography;

export default function ModalDesligarPessoa({ aberto, pessoa, onClose, onSalvo }: {
  aberto: boolean;
  pessoa: { id: string; nome: string; usuario_email?: string | null; admissao?: string | null; desligamento?: string | null };
  onClose: () => void;
  onSalvo: (r: any) => void;
}) {
  const { message } = AntApp.useApp();
  const [opcoes, setOpcoes] = useState<Opcoes | null>(null);
  const [data, setData] = useState<string>(pessoa.desligamento || dayjs().format('YYYY-MM-DD'));
  const [novoChefe, setNovoChefe] = useState<string | undefined>(undefined);
  const [semChefe, setSemChefe] = useState(false);
  const [cancelarFerias, setCancelarFerias] = useState(true);
  const [subordinados, setSubordinados] = useState<Array<{ id: string; nome: string }>>([]);
  const [horasDepois, setHorasDepois] = useState<Array<{ data: string; minutos: number }> | null>(null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!aberto) return;
    setSubordinados([]); setHorasDepois(null); setNovoChefe(undefined); setSemChefe(false);
    setData(pessoa.desligamento || dayjs().format('YYYY-MM-DD'));
    acaoPessoa<Opcoes>('pessoa_opcoes').then(setOpcoes).catch(() => setOpcoes(null));
  }, [aberto, pessoa]);

  const chefes = useMemo(() => (opcoes?.chefes || []).filter((c) => c.id !== pessoa.id)
    .map((c) => ({ value: c.id, label: `${c.nome}${c.setor ? ` · ${c.setor}` : ''}` })), [opcoes, pessoa.id]);

  async function confirmar() {
    if (!data) { message.warning('Informe a data de saída.'); return; }
    setSalvando(true);
    try {
      const r = await acaoPessoa('pessoa_desligar', {
        profissional_id: pessoa.id, data_saida: data, novo_chefe_profissional_id: novoChefe, sem_chefe: semChefe, cancelar_ferias: cancelarFerias,
      });
      onSalvo(r);
    } catch (e: any) {
      if (e instanceof ErroPessoa && e.dados?.codigo === 'subordinados') { setSubordinados(e.dados.subordinados || []); return; }
      if (e instanceof ErroPessoa && e.dados?.codigo === 'horas_depois') { setHorasDepois(e.dados.horas || []); return; }
      message.error(e.message, 8);
    } finally { setSalvando(false); }
  }

  const correcao = !!pessoa.desligamento;
  return (
    <Modal open={aberto} onCancel={() => !salvando && onClose()} onOk={confirmar} confirmLoading={salvando}
      okText={correcao ? 'Corrigir a data de saída' : 'Desligar'} okButtonProps={{ danger: !correcao }} cancelText="Voltar"
      title={correcao ? `Corrigir a saída de ${pessoa.nome}` : `Desligar ${pessoa.nome}`}
      width="94vw" style={{ maxWidth: 620, top: 24 }} destroyOnHidden>
      <Flex vertical gap={10} style={{ marginTop: 8 }}>
        <Text>
          A partir da data de saída a pessoa deixa de dever horas, sai da cobrança e do custo dos contratos, e as férias
          marcadas depois dela são canceladas. {pessoa.usuario_email ? 'O login é bloqueado na hora e os perfis de acesso são desativados.' : 'Esta ficha não tem login.'} O que já foi anotado continua contando.
        </Text>
        <div>
          <Text type="secondary" style={{ fontSize: 12 }}>Último dia de vínculo</Text>
          <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" value={data ? dayjs(data) : null}
            onChange={(d) => { setData(d ? d.format('YYYY-MM-DD') : ''); setHorasDepois(null); }}
            minDate={pessoa.admissao ? dayjs(pessoa.admissao) : undefined} />
          {pessoa.admissao && <Text type="secondary" style={{ fontSize: 11 }}>Admissão: {fmtData(pessoa.admissao)}</Text>}
        </div>
        {horasDepois && (
          <Alert type="error" showIcon message={`Há horas anotadas depois de ${fmtData(data)}`}
            description={`Dias: ${horasDepois.map((h) => fmtData(h.data)).join(', ')}. Corrija essas horas na semana da pessoa ou escolha uma data depois delas.`} />
        )}
        {subordinados.length > 0 && (
          <Alert type="warning" showIcon message={`${pessoa.nome} é chefe de ${subordinados.map((s) => s.nome).join(', ')}.`}
            description={(
              <Flex vertical gap={6}>
                <Select style={{ width: '100%' }} value={novoChefe} onChange={(v) => { setNovoChefe(v); setSemChefe(false); }} allowClear
                  showSearch optionFilterProp="label" options={chefes} placeholder="Novo chefe direto — escreva para buscar" />
                <Checkbox checked={semChefe} onChange={(e) => { setSemChefe(e.target.checked); if (e.target.checked) setNovoChefe(undefined); }}>
                  Deixar sem chefe por enquanto (a administração recebe os alertas deles)
                </Checkbox>
              </Flex>
            )} />
        )}
        <Checkbox checked={cancelarFerias} onChange={(e) => setCancelarFerias(e.target.checked)}>
          Cancelar férias marcadas depois da saída (e encurtar as que atravessam a data)
        </Checkbox>
      </Flex>
    </Modal>
  );
}

export function resumoDesligamento(r: any): string {
  const partes: string[] = [];
  partes.push(r.ativo_ate_a_data ? `fica ativa até ${fmtData(r.data_saida)}` : `inativa desde ${fmtData(r.data_saida)}`);
  if (r.perfis?.length) partes.push(`${r.perfis.length} perfil(is) de acesso desativado(s)`);
  if (r.ferias_apagadas) partes.push(`${r.ferias_apagadas} período(s) de férias cancelado(s)`);
  if (r.ferias_encurtadas) partes.push(`${r.ferias_encurtadas} período de férias encurtado`);
  if (r.subordinados_movidos) partes.push(`${r.subordinados_movidos} subordinado(s) passaram ao novo chefe`);
  if (r.avisos_desativados) partes.push('saiu das listas de aviso');
  const ctr = r.contratos_em_que_e_gestor_ou_coordenador || [];
  if (ctr.length) partes.push(`atenção: ainda é gestor/coordenador de ${ctr.map((c: any) => c.numero).join(', ')} — troque no kick-off do contrato`);
  return `${r.nome}: ${partes.join('; ')}.`;
}
